import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import { Divider, Popper, TextField, Typography } from '@mui/material';
import Autocomplete, { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';

import AutocompleteOptionWithChart from './AutocompleteOptionWithChart';

export interface ListItem {
    id: string;
    name: string;
    locked?: unknown;
}

interface ItemListAutocompleteProps {
    itemList: ListItem[] | null;
    setSelectedItem: (item: ListItem | null) => void;
    selectedItem: ListItem | null;
    onClickEdit: () => void;
    onClickView: () => void;
    groupBy?: (item: ListItem) => string;
}

const StyledPopper = styled(Popper)(({ theme }) => ({
    transform: 'none !important',
    '& .MuiAutocomplete-paper': {
        borderLeft: 'none',
        borderRight: 'none',
        boxShadow: 'none',
        backgroundColor: theme.palette.background.default,
        backgroundImage: 'none'
    },
    '& .MuiAutocomplete-option': {
        borderTop: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    '& .MuiAutocomplete-option:last-child, & .MuiAutocomplete-option:has(+ h5)': {
        borderBottom: 'none'
    },
    '& .MuiAutocomplete-option[data-focus="true"]': {
        backgroundColor: 'transparent !important'
    },
    '& .MuiAutocomplete-option:hover': {
        backgroundColor: `${theme.palette.action.hover} !important`
    }
}));

const ItemListAutocomplete = ({ itemList, setSelectedItem, selectedItem, onClickEdit, onClickView, groupBy }: ItemListAutocompleteProps) => {
    const {
        pageState: { pageConfig }
    } = usePageStore();

    function renderOption({ key, ...props }: React.HTMLAttributes<HTMLLIElement> & { key: React.Key }, option: ListItem) {
        function onDoubleClick() {
            if (!selectedItem) {
                return;
            }
            if (selectedItem.locked) {
                onClickView();
            } else {
                onClickEdit();
            }
        }

        return (
            <li key={key} {...props}>
                <AutocompleteOptionWithChart errorCodes={pageConfig.errorCodes} option={option} size="large" onDoubleClick={onDoubleClick} />
            </li>
        );
    }

    function getOptionLabel(option: ListItem) {
        return option.name;
    }

    function isOptionEqualToValue(option: ListItem, value: ListItem) {
        return option.id === value.id;
    }

    function onChange(_event: React.SyntheticEvent, item: ListItem | null) {
        setSelectedItem(item);
    }

    function renderInput(params: object) {
        return <TextField {...(params as React.ComponentProps<typeof TextField>)} label={pageConfig.autoCompletePlaceholder} variant="outlined" />;
    }

    function renderGroup({ key, group, children }: AutocompleteRenderGroupParams) {
        return (
            <React.Fragment key={key}>
                <Typography variant="h5" sx={{ mt: 2, mb: 0.5, px: 2, color: 'text.secondary', fontWeight: 200 }}>
                    {group}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {children}
            </React.Fragment>
        );
    }

    if (!itemList || (!itemList.length && !pageConfig.emptyMessage)) {
        return null;
    }

    return itemList.length ? (
        <Autocomplete
            ListboxProps={{
                style: { maxHeight: '100%' },
                onMouseLeave: (e) => {
                    (e.currentTarget as HTMLElement).querySelectorAll('[data-focus="true"]').forEach((el) => {
                        el.removeAttribute('data-focus');
                    });
                }
            }}
            value={selectedItem}
            options={itemList}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            onChange={onChange}
            renderInput={renderInput}
            open
            disablePortal
            fullWidth={true}
            PopperComponent={StyledPopper}
            renderOption={renderOption}
            groupBy={groupBy}
            renderGroup={groupBy ? renderGroup : undefined}
        />
    ) : (
        <Typography variant="subtitle2">{pageConfig.emptyMessage}</Typography>
    );
};

export default ItemListAutocomplete;
